/**
 * V55.64: HR 면담 전송 기록 훅
 *
 * HR 성과/평가현황 페이지에서 전송된 면담 기록을 조회합니다.
 *
 * @module shared/hooks/useHrInterviewShares
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';

// ============================================
// 타입 정의
// ============================================

/** 면담 타입 */
export type InterviewType =
  | 'regular'
  | 'probation'
  | 'performance'
  | 'resignation'
  | 'return'
  | 'feedback'
  | 'grievance'
  | 'etc';

/** 전송/합의 상태 */
export type DeliveryStatus = 'notSent' | 'sent' | 'agreed' | 'disagreed';

/** HR 면담 전송 기록 목록 아이템 */
export interface HrInterviewShareListItem {
  id: string;
  interviewId: string;
  targetUserId: string;
  targetUserName: string;
  targetOrgUnitName: string;
  interviewerName: string;
  interviewerOrgUnitName?: string;
  interviewDate: string;
  interviewTime: string;
  interviewType: InterviewType;
  interviewTypeLabel: string;
  deliveryStatus: DeliveryStatus;
  deliveryStatusLabel: string;
  sentAt: string;
  sentByName: string;
}

/** HR 면담 전송 기록 상세 */
export interface HrInterviewShareDetail {
  id: string;
  interviewId: string;
  companyCode: string;
  targetUserId: string;
  targetUserName: string;
  targetOrgUnitId: string;
  targetOrgUnitName: string;
  targetPositionTitle?: string;
  interviewerUserId: string;
  interviewerName: string;
  interviewerOrgUnitName?: string;
  interviewDate: string;
  interviewTime: string;
  location: string;
  interviewType: InterviewType;
  interviewTypeLabel: string;
  interviewStatus: string;
  deliveryStatus: DeliveryStatus;
  deliveryStatusLabel: string;
  contentHtml: string;
  interviewerSignaturePath?: string;
  targetSignaturePath?: string;
  sentAt: string;
  sentBy: string;
  sentByName: string;
  createdAt: string;
}

// ============================================
// useHrInterviewShares - 목록 조회
// ============================================

interface UseHrInterviewSharesResult {
  shares: HrInterviewShareListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * HR 면담 전송 기록 목록 조회 훅
 */
export function useHrInterviewShares(): UseHrInterviewSharesResult {
  const { user } = useCurrentUser();

  const [shares, setShares] = useState<HrInterviewShareListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const fetchShares = useCallback(async (append = false) => {
    if (!user || !functions) {
      setIsLoading(false);
      return;
    }

    if (!append) {
      setIsLoading(true);
    }
    setError(null);

    // V203: DEV 환경에서 debug 플래그 전달
    const IS_DEV = process.env.NODE_ENV === 'development';

    try {
      const listHrInterviewShares = httpsCallable<
        { limit?: number; cursor?: string; debug?: boolean },
        { success: boolean; shares: HrInterviewShareListItem[]; hasMore?: boolean; nextCursor?: string; error?: string }
      >(functions, 'listHrInterviewShares');

      const result = await listHrInterviewShares({
        limit: 50,
        cursor: append ? cursor : undefined,
        debug: IS_DEV, // V203: DEV 환경에서 디버그 정보 요청
      });

      if (result.data.success) {
        if (append) {
          setShares((prev) => [...prev, ...result.data.shares]);
        } else {
          setShares(result.data.shares);
        }
        setHasMore(result.data.hasMore || false);
        setCursor(result.data.nextCursor);
      } else {
        throw new Error(result.data.error || '인사팀 면담 기록 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.64] useHrInterviewShares error:', err);
      // V203: 에러 details를 JSON.stringify로 출력 (복사 가능)
      if (err.details) {
        console.error(
          '[V55.64] useHrInterviewShares error details (복사용 JSON):',
          JSON.stringify(err.details, null, 2)
        );
      }
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, cursor]);

  const loadMore = useCallback(async () => {
    if (hasMore && cursor) {
      await fetchShares(true);
    }
  }, [hasMore, cursor, fetchShares]);

  useEffect(() => {
    fetchShares(false);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    shares,
    isLoading,
    error,
    refetch: () => fetchShares(false),
    hasMore,
    loadMore,
  };
}

// ============================================
// useHrInterviewShareDetail - 상세 조회
// ============================================

interface UseHrInterviewShareDetailResult {
  share: HrInterviewShareDetail | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * HR 면담 전송 기록 상세 조회 훅
 *
 * @param shareId - hrInterviewShares 문서 ID
 */
export function useHrInterviewShareDetail(shareId: string | undefined): UseHrInterviewShareDetailResult {
  const { user } = useCurrentUser();

  const [share, setShare] = useState<HrInterviewShareDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!user || !functions || !shareId) {
      setShare(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getHrInterviewShareDetail = httpsCallable<
        { shareId: string },
        { success: boolean; share?: HrInterviewShareDetail; error?: string }
      >(functions, 'getHrInterviewShareDetail');

      const result = await getHrInterviewShareDetail({ shareId });

      if (result.data.success && result.data.share) {
        setShare(result.data.share);
      } else {
        throw new Error(result.data.error || '인사팀 면담 상세 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.64] useHrInterviewShareDetail error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, shareId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    share,
    isLoading,
    error,
    refetch: fetchDetail,
  };
}

// ============================================
// useSendInterviewToHr - 면담 HR 전송
// ============================================

/** V55.66: sendToHr 응답 타입 */
interface SendToHrResult {
  shareId?: string;
  alreadySent: boolean;
  /** 재전송 확인이 필요한 경우 true */
  requireConfirm?: boolean;
  message?: string;
}

interface UseSendInterviewToHrResult {
  /** V55.66: confirmResend 파라미터 추가 */
  sendToHr: (interviewId: string, confirmResend?: boolean) => Promise<SendToHrResult>;
  isSending: boolean;
  error: Error | null;
}

/**
 * 면담을 HR에게 전송하는 mutation 훅
 *
 * V55.66: confirmResend 파라미터 추가
 * - 첫 호출 시 confirmResend 없이 호출
 * - 응답에서 requireConfirm=true면 재전송 확인 팝업 표시
 * - 확인 시 confirmResend=true로 재호출
 */
export function useSendInterviewToHr(): UseSendInterviewToHrResult {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sendToHr = useCallback(async (interviewId: string, confirmResend?: boolean): Promise<SendToHrResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsSending(true);
    setError(null);

    try {
      const sendInterviewToHr = httpsCallable<
        { interviewId: string; confirmResend?: boolean },
        { success: boolean; shareId?: string; alreadySent: boolean; requireConfirm?: boolean; message?: string; error?: string }
      >(functions, 'sendInterviewToHr');

      const result = await sendInterviewToHr({ interviewId, confirmResend });

      if (result.data.success) {
        // V55.66: requireConfirm=true면 재전송 확인 필요 (shareId 없음)
        if (result.data.requireConfirm) {
          return {
            alreadySent: true,
            requireConfirm: true,
            message: result.data.message,
          };
        }

        // 실제 전송 완료
        return {
          shareId: result.data.shareId,
          alreadySent: result.data.alreadySent,
          message: result.data.message,
        };
      } else {
        throw new Error(result.data.error || '인사팀 전송 실패');
      }
    } catch (err: any) {
      console.error('[V55.64] useSendInterviewToHr error:', err);
      const error = err instanceof Error ? err : new Error(err.message || 'Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsSending(false);
    }
  }, []);

  return {
    sendToHr,
    isSending,
    error,
  };
}

// ============================================
// 상수
// ============================================

/** 면담 타입 라벨 */
export const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  regular: '정기 면담',
  probation: '수습사원 면담',
  performance: '성과/평가 면담',
  resignation: '퇴직 면담',
  return: '복귀 면담',
  feedback: '피드백 면담',
  grievance: '직원고충 면담',
  etc: '기타',
};

/** 전송 상태 라벨 */
export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  notSent: '미전송',
  sent: '미확인',
  agreed: '상호합의',
  disagreed: '미동의',
};
